import { formatTimestamp } from "../../utils/formatDate";
import { ArcGISFieldType, FieldInfo } from "../../interfaces/krookie";

export const isIntegerType = (
  type: ArcGISFieldType,
): type is "small-integer" | "integer" | "big-integer" | "long" => {
  return ["small-integer", "integer", "big-integer", "long"].includes(type);
};

export const isFloatType = (
  type: ArcGISFieldType,
): type is "single" | "double" => {
  return ["single", "double"].includes(type);
};

export const isStringType = (type: ArcGISFieldType): type is "string" => {
  return type === "string";
};

export const isDateType = (type: ArcGISFieldType): boolean => {
  return ["date", "date-only", "timestamp-offset"].includes(type);
};

export const processFieldValue = (
  value: string,
  fieldInfo: FieldInfo,
): string => {
  const { type, maxLength, codedValueDict } = fieldInfo;

  if (codedValueDict && Object.keys(codedValueDict).length > 0) {
    return value;
  }

  if (isStringType(type)) {
    if (maxLength && value.length > maxLength) {
      return value.substring(0, maxLength);
    }
    return value;
  }

  if (isIntegerType(type)) {
    const cleaned = value.replace(/[^\d-]/g, "");
    return cleaned.replace(/(?!^)-/g, "");
  }

  if (isFloatType(type)) {
    let cleaned = value.replace(/[^\d.-]/g, "");
    const dotIndex = cleaned.indexOf(".");
    if (dotIndex !== -1) {
      cleaned =
        cleaned.substring(0, dotIndex + 1) +
        cleaned.substring(dotIndex + 1).replace(/\./g, "");
    }
    cleaned = cleaned.replace(/(?!^)-/g, "");
    return cleaned;
  }

  return value;
};

export const getDisplayValue = (
  value: any,
  key: string,
  fieldsInfoMap: Map<string, FieldInfo> | null,
): string => {
  const fieldInfo = fieldsInfoMap?.get(key);
  const codedValues = fieldInfo?.codedValueDict;
  const hasCodedValues = codedValues && Object.keys(codedValues).length > 0;

  if (fieldInfo && isDateType(fieldInfo.type)) {
    return value ? formatTimestamp(Number(value)) : "";
  }

  if (hasCodedValues && codedValues[String(value)]) {
    return codedValues[String(value)];
  }
  return String(value ?? "");
};

export const validateDate = (value: string): boolean => {
  if (!value || value.trim() === "") {
    return false;
  }

  if (value.length !== 8) return true;

  const day = Number(value.substring(0, 2));
  const month = Number(value.substring(2, 4));
  const year = Number(value.substring(4, 8));

  if (isNaN(day) || isNaN(month) || isNaN(year)) return true;
  if (day < 1 || day > 31 || month < 1 || month > 12) return true;
  if ([4, 6, 9, 11].includes(month) && day > 30) return true;
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (day > (leap ? 29 : 28)) return true;
  }
  return false;
};

export const dateToTimestamp = (value: string): number => {
  const parts = value.split("-");
  const [d, m, y] = parts;
  const day = Number(d);
  const month = Number(m);
  const year = Number(y);
  const date = new Date(year, month - 1, day);
  return date.getTime();
};
